import {memo} from 'react'
import {useParams} from 'react-router-dom'
import {differenceInCalendarMonths, parseISO} from 'date-fns'
import dayjs from 'dayjs'
import {TinyColumn} from '@ant-design/plots'
import {Space, Table} from 'antd'

import styles from './RequestForecast.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {
  Block,
  Text,
  Button,
  Modal,
  Loader,
  UserAvatar,
  IconButton,
  IconTitle,
  RotateArrowFourthIcon,
  BlackboardChartFourthIcon,
  DotsIcon,
} from 'common/components'
// import {useGetForecast} from 'common/api'
import {
  formatDate,
  formatMoney,
  maybePluralize,
  isSameYearMonth,
  lastUpdatedItemFromArray,
  totalNumberFromArray,
  arrayFromMonthsCount,
  isEqual,
  isItemDrawdown,
} from 'common/helpers'
import {ForecastType, ForecastItem} from 'common/types'
import {VariationRequestForecastForm, isMilestonePassed} from '..'
import {useGetVariationRequest, useGetVariationTransactions, useGetVariationTransactionsConfirmed} from '../api.variation'

interface ForecastProps {
  creationDisabled: boolean
  forecast: ForecastItem[]
  request: any
  onForecastOpen?: () => void
}

interface SummaryRowProps {
  type: ForecastType
  forecast: ForecastItem[]
  fixedAmount?: number
}

interface ChartProps {
  months: number
  startDate: Date
  forecast: ForecastItem[]
}

export default function VariationRequestForecast({isConfirm, disabledModal}:{
  isConfirm?: boolean
  disabledModal?: boolean
}) {
  const params = useParams()
  const {data: request, isRefetching: isRefetchingRequest} = useGetVariationRequest(params.uuid)
  const { data: transactions1, isRefetching: isRefetchingTrans1} = useGetVariationTransactions(params.uuid, !isConfirm)
  const { data: transactions2, isRefetching: isRefetchingTrans2} = useGetVariationTransactionsConfirmed(params.uuid, !!isConfirm)
  // console.log(transactions1)
  // console.log(transactions2)
  // console.log(request)
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const isForecastOpen = useStore((state) => state.isVariationForecastOpen)
  const setForecastOpen = useStore((state) => state.setVariationForecastOpen)
  const handleForecastOpen = () => setForecastOpen(true)
  const handleForecastClose = () => setForecastOpen(false)
  const isRefetching = isRefetchingRequest || isRefetchingTrans1 || isRefetchingTrans2
  const creationDisabled = 
    !permissions?.includes('edit variation forecast') || isRefetching

  const forecast:any[] = (
    ( !isConfirm ? 
      request?.forecastItems : 
      request?.forecastItemsConfirmed
    ) || []
  ).sort(
    (a:any, b:any) => dayjs(a.date).isBefore(dayjs(b.date)) ? -1:1
  ).map((x:any) => ({
    /**
     * forecastItems have different props from forecastItemsConfirmed
     * so, unify the data structure here
     */ 
    ...x, // uuid, type
    date: parseISO(x.date || x.value?.date),
    amount: x.amount ?? x.value?.amount,
    initialDrawdown:!!x.initialDrawdown,
    isEst: true,
  }))
  // console.log(forecast)

  return (
    // !!isConfirm ? null :
    <>
      <Block relative type="white">
        { !isMilestonePassed(
            !isConfirm ? 'forecast-created':'forecast-confirmed', 
            request
          ) ? (
          <div className={styles.section}>
            <IconTitle icon={<BlackboardChartFourthIcon />}>Forecast</IconTitle>
            { !creationDisabled &&
              <Button
                variant="secondary"
                disabled={creationDisabled}
                onClick={handleForecastOpen}
              >
                create
              </Button>
            }
          </div>
        ) : isRefetching ? (
          <Loader />
        ) : (
          <Forecast
            creationDisabled={creationDisabled}
            forecast={forecast}
            request={request}
            onForecastOpen={!!disabledModal ? undefined:handleForecastOpen}
          />
        )}
      </Block>
      { !disabledModal &&
      <Modal
        // title={`Variation Forecast`}
        width={692}
        open={isForecastOpen}
        onCancel={handleForecastClose}
      >
        <Text mt={48} mb={16} size="2xl" weight="medium" color="black">Variation Forecast</Text>
        <Text mb={36} size="md" color="black">
          Please review and adjust the forecast. Note that the processed transactions and initial drawdown cannot be edited, but you can make edits or additions to the estimated drawdown and repayment forecast.
        </Text>
        <VariationRequestForecastForm
          isConfirm={isConfirm}
          request={request}
          transactions={isConfirm ? transactions2 : transactions1 }
          forecast={forecast}
          onClose={handleForecastClose}
        />
      </Modal>
      }
    </>
  )
}

const Forecast = memo(
  ({creationDisabled, forecast, request, onForecastOpen}: ForecastProps) => {
    const lastUpdatedItem = !!forecast?.length ? lastUpdatedItemFromArray(forecast) : null
    const updatedBy = lastUpdatedItem?.value?.createdBy

    const drawdownTransactions: any[] = request?.projectTransactions?.filter(
      (x:any) => ( isItemDrawdown(x) && (!!x.amount) && (!!x.date) )
    ).map((x:any) => ({
      date: parseISO(x.date),
      amount: x.amount || 0,
      type: x.type,
      // notes: x.notes,
      initialDrawdown: (x.type === 'initial-drawdown'),
    })).sort(
      (a:any, b:any) => ( dayjs(a.date).isBefore(dayjs(b.date)) ? -1:1 )
    ) || []

    const startDate = drawdownTransactions[0].date
    const endDate   = !!forecast?.length ? forecast.at(-1).date : drawdownTransactions.at(-1).date
    const months    = ((!!startDate) && (!!endDate)) ? differenceInCalendarMonths(endDate, startDate) : 0

    // console.table(forecast)
    // console.table(drawdownTransactions)
    const rows = getForecastData([
      ...drawdownTransactions,
      ...forecast,
    ])
    // console.table(rows)

    const fixedTransTotal = !drawdownTransactions?.length ? 0 : totalNumberFromArray(drawdownTransactions, 'amount')

    return (
      !forecast ? null :
      <>
        {!creationDisabled && !!onForecastOpen && (
          <IconButton className={styles.edit} onClick={onForecastOpen}>
            <DotsIcon />
          </IconButton>
        )}
        <div className={styles.header}>
          <IconTitle icon={<BlackboardChartFourthIcon />} mb={16}>
            Forecast
          </IconTitle>
          <div className={styles.block}>
            <Space size={12}>
              <UserAvatar
                src={updatedBy?.photo}
                size={32}
                alt={updatedBy?.firstName}
              />
              <Text as="span" weight="medium" size="sm" color="grey">
                has been updated{' '}
                {!!lastUpdatedItem?.updatedAt ? formatDate(lastUpdatedItem.updatedAt, 'third') : ''}
                <br />
                by{' '}
                { !!updatedBy
                  ? `${updatedBy.firstName} ${updatedBy.lastName}`
                  : ''}
              </Text>
            </Space>
            <Chart months={months} startDate={startDate} forecast={rows} />
          </div>
        </div>
        <Table
          className={styles.table}
          showHeader={false}
          pagination={false}
          columns={columns}
          dataSource={rows}
          summary={() => (
            <>
              <SummaryRow type="drawdown" forecast={forecast} fixedAmount={fixedTransTotal} />
              <SummaryRow type="repayment" forecast={forecast} />
            </>
          )}
        />
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
)

const SummaryRow = ({type, forecast, fixedAmount = 0}: SummaryRowProps) => {
  const filteredItems = forecast.filter((item) => item.type === type)

  if (!filteredItems.length) return null

  const items = filteredItems  // .map((subItem) => subItem.value)
  const totalAmount = totalNumberFromArray(items, 'amount')
  const isDrawdown = isItemDrawdown({type})
  const title = maybePluralize(items.length, type)

  return (
    <Summary.Row>
      <Summary.Cell index={0} />
      <Summary.Cell index={1}>
        {isDrawdown ? (
          title
        ) : (
          <Space>
            <RotateArrowFourthIcon />
            {title}
          </Space>
        )}
      </Summary.Cell>
      <Summary.Cell index={2} colSpan={2} align="right">
        <Text size="3xl" weight="medium">
          {formatMoney(totalAmount + fixedAmount)}
        </Text>
      </Summary.Cell>
    </Summary.Row>
  )
}

const Chart = memo(
  ({months, startDate, forecast}: ChartProps) => {
    const monthsArray = arrayFromMonthsCount(months, startDate)
    const drawdowns = forecast.filter((item) => isItemDrawdown(item))
    const data: number[] = []
    monthsArray.forEach((date) => {
      data.push(
        drawdowns.some((item) => isSameYearMonth(item.date, date))
          ? totalNumberFromArray(
              drawdowns
                .filter((item) => isSameYearMonth(item.date, date))
                .map((item:any) => item.amount)
            )
          : 0
      )
    })

    const config: any = {
      data,
      width: 148,
      height: 38,
      color: '#b4d8d8',
      maxColumnWidth: 4,
      columnStyle: ({y}: any) =>
        y ? {radius: 6, stroke: '#569695'} : undefined,
    }

    return <TinyColumn {...config} />
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
)

const {Summary} = Table

interface ForecastItemEx extends ForecastItem {
  isEst?: boolean
} 

const getForecastData = (items: ForecastItemEx[]) => {
  let drawdownCount = 0
  let repaymentCount = 1
  return items.map((item, index) => ({
    ...item,
    key: item.uuid || index.toString(),
    number: 
      ((!!item.initialDrawdown) && (item.isEst) && (drawdownCount !== 0)) ? drawdownCount :
      isItemDrawdown(item) ? drawdownCount++ : repaymentCount++,
    type: item.type,
    date: item.date,
    prevDate:
      index === 0 ? undefined : items[index - 1].date,
  }))
}

const columns = [
  {
    dataIndex: 'date',
    render: (value: Date, record: any) => {
      if (isSameYearMonth(record.prevDate, value))
        return <div className={styles.line} />
      return formatDate(value, 'fourth')
    },
  },
  {
    dataIndex: 'type',
    render: (value: ForecastType, record: any) => {
      const content =
        ((value !== 'repayment') && (record.number === 0)) ? 'Initial drawdown' : 
        ((!!record.initialDrawdown) && (!!record.isEst)) ? 'Est. variation initial drawdown' : 
        [`${!!record.isEst ? 'Est.':''}`, `${value} ${record.number}`].filter((x:any) => (!!x)).join(' ')
      return value === 'repayment' ? (
        <Space>
          <RotateArrowFourthIcon />
          <span>{content}</span>
        </Space>
      ) : (
        content
      )
    },
  },
  {
    dataIndex: 'date',
    render: (value: Date) => (
      <Text size="xs" color="grey" align="right">
        {formatDate(value, 'third')}
      </Text>
    ),
  },
  {
    dataIndex: 'amount',
    render: (value: number, record: any) => {
      const content = formatMoney(value, {prefix: false})
      return (
        <Text weight="demiBold" align="right">
          {record.type === 'repayment' ? (
            <>
              <Text as="span" color="grey">
                (
              </Text>
              {content}
              <Text as="span" color="grey">
                )
              </Text>
            </>
          ) : (
            content
          )}
        </Text>
      )
    },
  },
]
